import { EditorConfig, OutputData } from '@editorjs/editorjs';
import { Blocks, Toolbar } from '@editorjs/editorjs/types/api';

type DragDropConstructor = {
  configuration: EditorConfig;
  blocks: Blocks;
  toolbar: Toolbar;
  save: () => Promise<OutputData>;
};

export default class DragDrop {
  private readonly toolbar: Toolbar;
  private readonly borderStyle: string;
  private readonly api: Blocks;
  private readonly holder: HTMLElement | null;
  private readonly readOnly: boolean | undefined;
  private startBlock: number | null;
  private endBlock: number | null;
  private readonly save: () => Promise<OutputData>;

  constructor(
    { configuration, blocks, toolbar, save }: DragDropConstructor,
    borderStyle?: string,
  ) {
    this.toolbar = toolbar;
    this.borderStyle = borderStyle || '1px dashed #aaa';
    this.api = blocks;
    this.holder =
      typeof configuration.holder === 'string'
        ? document.getElementById(configuration.holder)
        : configuration.holder || null;
    this.readOnly = configuration.readOnly;
    this.startBlock = null;
    this.endBlock = null;
    this.save = save;

    this.setDragListener();
    this.setDropListener();
  }

  /**
   * Sets the cursor at the begining of the drag element
   */
  private setElementCursor(element: HTMLElement | null): void {
    if (!element) return;
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStart(element.childNodes[0], 0);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    element.focus();
  }

  /**
   * Sets the drag events listener.
   */
  private setDragListener(): void {
    if (!this.readOnly) {
      const settingsButton = this.holder?.querySelector(
        '.ce-toolbar__settings-btn',
      ) as HTMLElement | null;
      if (settingsButton) {
        this.initializeDragListener(settingsButton);
      } else {
        // If there's no settings button, we wait for it to be added to the DOM
        const observer = new MutationObserver((mutations, obs) => {
          const settingsButton = this.holder?.querySelector(
            '.ce-toolbar__settings-btn',
          ) as HTMLElement | null;
          if (settingsButton) {
            this.initializeDragListener(settingsButton);
            obs.disconnect();
          }
        });

        if (this.holder) {
          observer.observe(this.holder, {
            childList: true,
            subtree: true,
          });
        }
      }
    }
  }

  private initializeDragListener(settingsButton: HTMLElement): void {
    settingsButton.setAttribute('draggable', 'true');
    settingsButton.addEventListener('dragstart', () => {
      this.startBlock = this.api.getCurrentBlockIndex();
    });
    settingsButton.addEventListener('drag', () => {
      this.toolbar.close();
      if (!this.isTheOnlyBlock()) {
        const allBlocks = this.holder?.querySelectorAll(
          '.ce-block',
        ) as NodeListOf<Element>;
        const blockFocused = this.holder?.querySelector(
          '.ce-block--drop-target',
        ) as HTMLElement | null;
        this.setElementCursor(blockFocused);
        this.setBorderBlocks(allBlocks, blockFocused);
      }
    });
  }

  /**
   * Sets dinamically the borders in the blocks when a block is dragged
   * @param {object} allBlocks Contains all the blocks in the holder
   * @param {HTMLElement} blockFocused Is the element where the dragged element will be dropped.
   */

  private setBorderBlocks(
    allBlocks: NodeListOf<Element>,
    blockFocused: Element | null,
  ): void {
    Object.values(allBlocks).forEach((block: Element) => {
      const blockContent = block.querySelector(
        '.ce-block__content',
      ) as HTMLElement;
      if (block !== blockFocused) {
        blockContent.style.removeProperty('border-top');
        blockContent.style.removeProperty('border-bottom');
      } else {
        const index = Object.keys(allBlocks).find(
          key => allBlocks[Number(key)] === blockFocused,
        );
        if (
          index !== undefined &&
          this.startBlock !== null &&
          Number(index) > this.startBlock
        )
          blockContent.style.borderBottom = this.borderStyle;
        else blockContent.style.borderTop = this.borderStyle;
      }
    });
  }

  /**
   * Sets the drop events listener.
   */
  private setDropListener(): void {
    document.addEventListener('drop', (event: DragEvent) => {
      const { target } = event;
      if (this.holder?.contains(target as Node) && this.startBlock !== null) {
        const dropTarget = this.getDropTarget(target);
        if (dropTarget) {
          const blockContent = dropTarget.querySelector(
            '.ce-block__content',
          ) as HTMLElement;
          blockContent.style.removeProperty('border-top');
          blockContent.style.removeProperty('border-bottom');
          this.endBlock = this.getTargetPosition(dropTarget);
          this.moveBlocks();
        }
      }
      this.startBlock = null;
    });
  }

  /**
   * Notify core that read-only mode is suppoorted
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported(): boolean {
    return true;
  }

  /**
   * Returns the closest block DOM element to the drop event target.
   *
   * @param {HTMLElement} target  DOM element which received the drop event.
   * @returns {HTMLElement}
   */
  private getDropTarget(target: EventTarget | null): HTMLElement | null {
    const element = target as HTMLElement;
    return element?.classList.contains('ce-block')
      ? element
      : (element?.closest('.ce-block') as HTMLElement | null);
  }

  /**
   * Returns the target position in the child subtree.
   *
   * @param {HTMLElement} target  DOM element which received the drop event.
   * @returns {Number}
   */
  private getTargetPosition(target: HTMLElement): number {
    return target.parentNode
      ? Array.from(target.parentNode.children).indexOf(target)
      : 0;
  }

  private isTheOnlyBlock(): boolean {
    return this.api.getBlocksCount() === 1;
  }

  /**
   * Moves the dragged element to the drop position.
   *
   * @see {@link https://editorjs.io/blocks#move}
   */
  private moveBlocks(): void {
    if (this.isTheOnlyBlock()) return;
    if (this.endBlock === null || this.startBlock === null) return;
    this.api.move(this.endBlock, this.startBlock);
  }
}
